/**
 * Account deletion request / cancel (TMMAPP-0012, cooling-off), and Delete now (TMMAPI-0157,
 * TMMAPP-0119 §E 26c): `{immediate: true, confirmation: <the account's email>}` erases in the
 * request.
 */
import type { AccountDeletionBody, DeletionRequestView, ImmediateErasureView } from './generated';
import { request } from './client';

/** Delete now's answer: `200 {erased: true}`, or `202 {erased: false, retrying: true}`. */
export interface ImmediateErasure {
  erased: boolean;
  retrying: boolean;
  erasedAt: string | null;
}

export function normalizeImmediateErasure(w: ImmediateErasureView): ImmediateErasure {
  return {
    erased: w.erased,
    retrying: w.retrying ?? false,
    erasedAt: w.erasedAt ?? null,
  };
}

export type DeletionStatus = 'PENDING' | 'CANCELLED' | 'ERASING' | 'ERASED' | 'FAILED';

export interface DeletionRequest {
  id: number;
  status: DeletionStatus;
  requestedAt: string;
  coolingOffUntil: string;
  erasedAt: string | null;
}

const STATUSES: ReadonlySet<string> = new Set([
  'PENDING',
  'CANCELLED',
  'ERASING',
  'ERASED',
  'FAILED',
]);

/** An unknown status (one added to the contract later) reads as PENDING. */
export function normalizeDeletionRequest(w: DeletionRequestView): DeletionRequest {
  return {
    id: w.id,
    status: STATUSES.has(w.status) ? w.status : 'PENDING',
    requestedAt: w.requestedAt,
    coolingOffUntil: w.coolingOffUntil,
    erasedAt: w.erasedAt ?? null,
  };
}

export const accountDeletionApi = {
  async request(): Promise<DeletionRequest> {
    return normalizeDeletionRequest(
      await request<DeletionRequestView>('POST', '/v1/me/account-deletion'),
    );
  },
  /** Erase now; a confirmation that isn't the account's email is `422 CONFIRMATION_REQUIRED`. */
  async eraseNow(confirmation: string): Promise<ImmediateErasure> {
    const body: AccountDeletionBody = { immediate: true, confirmation };
    return normalizeImmediateErasure(
      await request<ImmediateErasureView>('POST', '/v1/me/account-deletion', { json: body }),
    );
  },
  async cancel(): Promise<DeletionRequest> {
    return normalizeDeletionRequest(
      await request<DeletionRequestView>('DELETE', '/v1/me/account-deletion'),
    );
  },
};
